class MovieCarousel {
  constructor(section) {
    this.section = section;
    this.tabs = section.querySelectorAll('.movie-carousel-tab');
    this.panels = section.querySelectorAll('.movie-carousel-panel');
    this.tracks = section.querySelectorAll('.movie-carousel-track');
    this.arrows = section.querySelectorAll('.movie-carousel-arrow');
    this.dotsContainers = section.querySelectorAll('.movie-carousel-dots');

    this.initTabs();
    this.initCarousels();
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        this.tabs.forEach((t) => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        this.panels.forEach((p) => {
          p.classList.toggle('is-active', p.dataset.panel === target);
        });
        this.tabs.forEach((t) => t.setAttribute('aria-selected', t === tab));
        this.initCarousels();
      });
    });
    const activeTab = this.section.querySelector('.movie-carousel-tab.is-active');
    if (activeTab) activeTab.setAttribute('aria-selected', 'true');
  }

  initCarousels() {
    this.tracks.forEach((track) => {
      const panel = track.closest('.movie-carousel-panel');
      if (!panel.classList.contains('is-active')) return;

      const container = track.closest('.movie-carousel-viewport');
      const sectionEl = track.closest('.movie-carousel-section');
      const arrows = sectionEl.querySelectorAll('.movie-carousel-arrow');
      const dotsContainer = sectionEl.querySelector('.movie-carousel-dots');

      this.currentTrack = track;
      this.currentContainer = container;
      this.updateArrows(arrows);
      this.buildDots(dotsContainer);
      this.updateDots(dotsContainer);

      if (!track._listeners) {
        track._listeners = true;

        track.addEventListener('scroll', () => {
          this.updateArrows(arrows);
          this.updateDots(dotsContainer);
        });

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        track.addEventListener('mousedown', (e) => {
          isDown = true;
          startX = e.pageX - track.offsetLeft;
          scrollLeft = track.scrollLeft;
          track.style.cursor = 'grabbing';
        });

        track.addEventListener('mouseleave', () => {
          isDown = false;
          track.style.cursor = '';
        });

        track.addEventListener('mouseup', () => {
          isDown = false;
          track.style.cursor = '';
        });

        track.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - track.offsetLeft;
          const walk = (x - startX) * 1.5;
          track.scrollLeft = scrollLeft - walk;
        });
      }

      arrows.forEach((arrow) => {
        const dir = arrow.dataset.dir;
        const newFn = () => {
          const card = track.querySelector('.movie-carousel-card');
          if (!card) return;
          const gap = parseFloat(getComputedStyle(track).gap) || 20;
          const scrollAmt = card.offsetWidth + gap;
          if (dir === 'prev') {
            track.scrollBy({ left: -scrollAmt, behavior: 'smooth' });
          } else {
            track.scrollBy({ left: scrollAmt, behavior: 'smooth' });
          }
        };
        if (arrow._clickFn) arrow.removeEventListener('click', arrow._clickFn);
        arrow._clickFn = newFn;
        arrow.addEventListener('click', arrow._clickFn);
      });
    });
  }

  buildDots(container) {
    if (!container) return;
    const activePanel = this.section.querySelector('.movie-carousel-panel.is-active');
    if (!activePanel) return;
    const track = activePanel.querySelector('.movie-carousel-track');
    if (!track) return;
    const cards = track.querySelectorAll('.movie-carousel-card');
    const visible = this.getVisibleCount(track);
    const dotCount = Math.max(1, Math.ceil(cards.length / visible) - 1);
    container.innerHTML = '';
    for (let i = 0; i <= dotCount; i++) {
      const dot = document.createElement('button');
      dot.className = 'movie-carousel-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.addEventListener('click', () => {
        const card = track.querySelector('.movie-carousel-card');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(track).gap) || 20;
        const scrollAmt = card.offsetWidth + gap;
        track.scrollTo({ left: scrollAmt * i, behavior: 'smooth' });
      });
      container.appendChild(dot);
    }
  }

  updateDots(container) {
    if (!container) return;
    const activePanel = this.section.querySelector('.movie-carousel-panel.is-active');
    if (!activePanel) return;
    const track = activePanel.querySelector('.movie-carousel-track');
    if (!track) return;
    const dots = container.querySelectorAll('.movie-carousel-dot');
    const visible = this.getVisibleCount(track);
    const card = track.querySelector('.movie-carousel-card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(track).gap) || 20;
    const scrollAmt = card.offsetWidth + gap;
    const currentIdx = Math.round(track.scrollLeft / scrollAmt);
    dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIdx));
  }

  getVisibleCount(track) {
    const card = track.querySelector('.movie-carousel-card');
    if (!card) return 1;
    const gap = parseFloat(getComputedStyle(track).gap) || 20;
    return Math.max(1, Math.floor((track.offsetWidth + gap) / (card.offsetWidth + gap)));
  }

  updateArrows(arrows) {
    const activePanel = this.section.querySelector('.movie-carousel-panel.is-active');
    if (!activePanel) return;
    const track = activePanel.querySelector('.movie-carousel-track');
    if (!track) return;
    arrows.forEach((arrow) => {
      const dir = arrow.dataset.dir;
      if (dir === 'prev') {
        arrow.classList.toggle('is-disabled', track.scrollLeft <= 10);
      } else {
        arrow.classList.toggle('is-disabled', track.scrollLeft >= track.scrollWidth - track.offsetWidth - 10);
      }
    });
  }
}

class MovieCart {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        this.add(addBtn.dataset.variantId, addBtn);
      }

      const plusBtn = e.target.closest('[data-plus-cart]');
      if (plusBtn) {
        e.preventDefault();
        this.add(plusBtn.dataset.variantId, plusBtn);
      }
    });
  }

  add(variantId, btn) {
    if (!variantId) return;
    const originalText = btn.textContent.trim();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart error');
        return res.json();
      })
      .then(() => {
        btn.textContent = 'ADDED';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        setTimeout(() => { btn.textContent = originalText; }, 1500);
      })
      .catch(() => {
        btn.textContent = 'ERROR';
        setTimeout(() => { btn.textContent = originalText; }, 1500);
      });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-movie-carousel]').forEach((el) => new MovieCarousel(el));
  new MovieCart();
});
